'use client';

import React, {FC, PropsWithChildren} from 'react';
import {usePathname, useSearchParams} from 'next/navigation';

import getIsMobileCompressed from '../../utils/getIsMobileCompressed';

import LayoutControls from './components/LayoutControls';
import LayoutFooter from './components/LayoutFooter';
import LayoutFooterMobile from './components/LayoutFooterMobile';
import LayoutPrivacyWidget from './components/LayoutPrivacyWidget';
import {
  getContainerClassName,
  getContinueBtnLabel,
  getStepClassName,
  getWrapperClassName,
  getWrapperStepClassName,
} from './utils';

import styles from './styles.module.scss';

export interface LayoutProps {
  progress: false | number;
  // TODO: look for a better (generic) way to type this: 'intro' | 'animation'
  currentStep: string;
  continueButton: {
    isVisible: boolean;
    isValid: boolean;
  };
  ui?: {
    hideFooter?: boolean;
    hideBackButton?: boolean;
    hidePrivacyWidget?: boolean;
    fullScreenStepWrapper?: boolean;
    inlineBackMobile?: boolean;
  };
  prev: () => void;
  next: () => void;
}

const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  progress,
  currentStep,
  continueButton,
  ui,
  children,
  prev,
  next,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryParams = Object.fromEntries(searchParams ?? new URLSearchParams());

  const isNerdwallet = pathname.includes('/forms/nerdwallet');

  const stringPercentage = `${progress}%`;

  const isAboutStep = currentStep === 'about';

  const currentYear = new Date().getFullYear();
  const continueBtnLabel = getContinueBtnLabel(isAboutStep);

  const iframeHost = queryParams.embed_host || undefined;

  // To reduce scrolling for embedded iframe on third party websites
  const isMobileCompressed = getIsMobileCompressed(iframeHost);

  return (
    <div className={getContainerClassName(ui)}>
      <LayoutPrivacyWidget show={ui?.hidePrivacyWidget !== true} />
      <div className={getStepClassName(ui)}>
        {currentStep === 'intro' ? (
          children
        ) : (
          <div className={getWrapperClassName(currentStep, ui)}>
            {progress !== false && (
              <div className={styles.stepWrapper__progress}>
                <div
                  className={styles.stepWrapper__progress_fill}
                  style={{width: stringPercentage}}>
                  {stringPercentage}
                </div>
              </div>
            )}

            <div className={getWrapperStepClassName(ui)}>{children}</div>

            <LayoutControls
              currentStep={currentStep}
              continueButton={continueButton}
              continueBtnLabel={continueBtnLabel}
              ui={ui}
              isAboutStep={isAboutStep}
              prev={prev}
              next={next}
            />

            <LayoutFooterMobile
              show={!isMobileCompressed}
              currentStep={currentStep}
            />
          </div>
        )}
      </div>
      <LayoutFooter
        show={ui?.hideFooter !== true}
        currentYear={currentYear}
        isNerdwallet={isNerdwallet}
      />
    </div>
  );
};

export default Layout;
